import { useCallback, useEffect, useState } from "react";
import apiClient from "../../../utils/apiClient";

const base = (studentId) => `/student-profile/students/${studentId}/evaluation`;

/** The sentence a refused move carries, with every problem when it lists them. */
export const evaluationProblem = (err, fallback = "Something went wrong. Try again.") => {
  const data = err?.response?.data;
  if (!data) return "The connection dropped. Nothing changed — try again.";
  return data.message || fallback;
};

/**
 * The office's side of one student's evaluation: the current row and the
 * moves on it. Each move resolves with the fresh evaluation and rethrows a
 * refusal (with the server's sentence under `message`) so the tab can show it
 * where it happened.
 */
export default function useEvaluation(studentId, enabled = true) {
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    if (!enabled || !studentId) { setEvaluation(null); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await apiClient.get(base(studentId));
      setEvaluation(data.evaluation || null);
      setError("");
    } catch (err) {
      setError(evaluationProblem(err, "Could not load the evaluation."));
    } finally {
      setLoading(false);
    }
  }, [studentId, enabled]);

  useEffect(() => { reload(); }, [reload]);

  const run = useCallback(async (label, fn) => {
    setBusy(label);
    setError("");
    try {
      const { data } = await fn();
      setEvaluation(data.evaluation || null);
      return data.evaluation;
    } catch (err) {
      const message = evaluationProblem(err);
      setError(message);
      throw Object.assign(new Error(message), { problems: err?.response?.data?.problems, code: err?.response?.data?.code });
    } finally {
      setBusy("");
    }
  }, []);

  return {
    evaluation,
    loading,
    busy,
    error,
    reload,
    clearError: () => setError(""),
    submit: () => run("submit", () => apiClient.post(`${base(studentId)}/submit`)),
    save: (body) => run("save", () => apiClient.put(base(studentId), body)),
    verifyDetails: (verified) => run("verify", () => apiClient.post(`${base(studentId)}/verify-details`, { verified })),
    act: (action, note) => run(action, () => apiClient.post(`${base(studentId)}/${action}`, note ? { note } : {})),
    sign: (body) => run("sign", () => apiClient.post(`${base(studentId)}/sign`, body)),
    pdfUrl: `${base(studentId)}/agreement.pdf`,
  };
}

/** The agreement PDF's bytes, for PdfView — through apiClient, so the token rides along. */
export async function fetchPdfBytes(url) {
  const { data } = await apiClient.get(url, { responseType: "arraybuffer" });
  return new Uint8Array(data);
}
